/**
 * Canonical research-area taxonomy — ONE controlled vocabulary shared by the
 * shop categories (Saleor slugs), the compare hub, the peptide lexicon and
 * the guide filters. Do not create parallel label tables elsewhere.
 *
 * Slugs mirror the live Saleor child categories of `peptides`, plus
 * editorial-only areas (`immune`) that have no shop category by design.
 */

import { getContentFallbackChainOf } from "@/config/locale";

export const RESEARCH_AREA_SLUGS = [
	"weight-management",
	"growth-hormone",
	"recovery",
	"cognition",
	"skin",
	"anti-aging",
	"hormonal",
	"immune",
] as const;

export type ResearchAreaSlug = (typeof RESEARCH_AREA_SLUGS)[number];

type ResearchAreaText = string | { label: string; heading: string };

const RESEARCH_AREA_LABELS: Record<ResearchAreaSlug, Record<string, ResearchAreaText>> = {
	"weight-management": {
		de: { label: "Gewichtsmanagement", heading: "Peptide und Forschungsstoffe für Gewichtsmanagement" },
		en: { label: "Weight Management", heading: "Weight Management Research Materials" },
		bg: { label: "Контрол на теглото", heading: "Пептиди за контрол на теглото" },
		bs: { label: "Kontrola tjelesne mase", heading: "Peptidi za kontrolu tjelesne mase" },
		cs: { label: "Regulace hmotnosti", heading: "Výzkumné peptidy pro regulaci hmotnosti" },
		da: { label: "Vægtkontrol", heading: "Forskningsmaterialer til vægtkontrol" },
		el: { label: "Διαχείριση βάρους", heading: "Πεπτίδια για διαχείριση βάρους" },
		es: { label: "Control de peso", heading: "Péptidos para control de peso" },
		et: { label: "Kehakaalu juhtimine", heading: "Kehakaalu juhtimise uurimispeptiidid" },
		fi: { label: "Painonhallinta", heading: "Painonhallinnan tutkimusmateriaalit" },
		fr: { label: "Gestion du poids", heading: "Peptides pour la gestion du poids" },
		hr: { label: "Kontrola tjelesne mase", heading: "Peptidi za kontrolu tjelesne mase" },
		hu: { label: "Testsúly", heading: "Peptidek testsúlykontroll-kutatáshoz" },
		it: { label: "Gestione del peso", heading: "Peptidi per la gestione del peso" },
		lt: { label: "Svorio kontrolė", heading: "Peptidai svorio kontrolei tirti" },
		lv: { label: "Svara kontrole", heading: "Peptīdi svara kontroles pētījumiem" },
		mk: { label: "Контрола на телесна тежина", heading: "Пептиди за контрола на телесната тежина" },
		nb: { label: "Vektkontroll", heading: "Peptider for vektkontroll" },
		nl: { label: "Gewichtsbeheer", heading: "Peptiden voor gewichtsbeheer" },
		pl: { label: "Masa ciała", heading: "Badania nad kontrolą masy ciała" },
		pt: { label: "Gestão do peso", heading: "Péptidos para gestão do peso" },
		"pt-br": { label: "Controle do peso", heading: "Peptídeos para pesquisa em controle do peso" },
		ro: { label: "Greutate", heading: "Peptide pentru controlul greutății" },
		sk: { label: "Regulácia hmotnosti", heading: "Regulácia hmotnosti" },
		sl: { label: "Nadzor telesne mase", heading: "Peptidi za nadzor telesne mase" },
		sr: { label: "Kontrola telesne mase", heading: "Peptidi za kontrolu telesne mase" },
		sv: { label: "Viktkontroll", heading: "Peptider för viktkontroll" },
		tr: { label: "Kilo yönetimi", heading: "Kilo yönetimi araştırma peptitleri" },
	},
	"growth-hormone": {
		de: { label: "Wachstumshormon", heading: "Wachstumshormon-Peptide: GHRH und GHRP" },
		en: { label: "Growth Hormone", heading: "Growth Hormone Research Peptides" },
		bg: { label: "Растежен хормон", heading: "Пептиди за растежен хормон" },
		bs: { label: "Hormon rasta", heading: "Peptidi hormona rasta" },
		cs: { label: "Osa růstového hormonu", heading: "Peptidy růstového hormonu: GHRH analoga a sekretagoga" },
		da: { label: "Væksthormon", heading: "Væksthormon-peptider til forskning" },
		el: { label: "Αυξητική ορμόνη", heading: "Πεπτίδια αυξητικής ορμόνης" },
		es: { label: "Hormona del crecimiento", heading: "Péptidos del eje de la hormona del crecimiento" },
		et: { label: "Kasvuhormoon", heading: "Kasvuhormooni peptiidid" },
		fi: { label: "Kasvuhormoni", heading: "Kasvuhormonipeptidit tutkimukseen" },
		fr: { label: "Hormone de croissance", heading: "Peptides de l’axe de l’hormone de croissance" },
		hr: { label: "Hormon rasta", heading: "Peptidi hormona rasta" },
		hu: { label: "Növekedési hormon", heading: "Peptidek a növekedési hormon kutatásához" },
		it: { label: "Ormone della crescita", heading: "Peptidi per la ricerca sull’ormone della crescita" },
		lt: { label: "Augimo hormonas", heading: "Augimo hormono peptidai" },
		lv: { label: "Augšanas hormons", heading: "Augšanas hormona peptīdi" },
		mk: { label: "Хормон на раст", heading: "Пептиди на хормонот на раст" },
		nb: { label: "Veksthormon", heading: "Veksthormonpeptider" },
		nl: { label: "Groeihormoon", heading: "Groeihormoonpeptiden" },
		pl: { label: "Hormon wzrostu", heading: "Peptydy osi hormonu wzrostu" },
		pt: { label: "Hormona de crescimento", heading: "Péptidos da hormona de crescimento" },
		"pt-br": {
			label: "Hormônio do crescimento",
			heading: "Peptídeos para pesquisa do hormônio do crescimento",
		},
		ro: { label: "Hormon de creștere", heading: "Peptide pentru cercetarea hormonului de creștere" },
		sk: { label: "Rastový hormón", heading: "Rastový hormón" },
		sl: { label: "Rastni hormon", heading: "Peptidi rastnega hormona" },
		sr: { label: "Hormon rasta", heading: "Peptidi hormona rasta" },
		sv: { label: "Tillväxthormon", heading: "Tillväxthormonpeptider" },
		tr: { label: "Büyüme hormonu", heading: "Büyüme hormonu peptitleri" },
	},
	recovery: {
		de: { label: "Regeneration", heading: "Regenerationspeptide und Forschungsstoffe" },
		en: { label: "Recovery", heading: "Recovery Research Materials" },
		bg: { label: "Възстановяване", heading: "Пептиди за изследвания на възстановяването" },
		bs: { label: "Oporavak i regeneracija", heading: "Peptidi za oporavak i regeneraciju" },
		cs: { label: "Regenerace", heading: "Regenerační peptidy pro výzkum tkání" },
		da: { label: "Restitution", heading: "Peptider til vævsforskning" },
		el: { label: "Αποκατάσταση", heading: "Πεπτίδια έρευνας για αποκατάσταση" },
		es: { label: "Recuperación", heading: "Péptidos para investigación de recuperación" },
		et: { label: "Taastumine ja regeneratsioon", heading: "Taastumise ja regeneratsiooni uurimispeptiidid" },
		fi: { label: "Palautuminen", heading: "Palautumisen tutkimusmateriaalit" },
		fr: { label: "Récupération", heading: "Peptides de récupération et de régénération" },
		hr: { label: "Oporavak i regeneracija", heading: "Peptidi za oporavak i regeneraciju" },
		hu: { label: "Regeneráció", heading: "Peptidek és keverékek regenerációkutatáshoz" },
		it: { label: "Rigenerazione tissutale", heading: "Peptidi per la ricerca sulla rigenerazione tissutale" },
		lt: { label: "Atsigavimas ir regeneracija", heading: "Peptidai atsigavimo ir regeneracijos tyrimams" },
		lv: {
			label: "Atjaunošanās un reģenerācija",
			heading: "Peptīdi atjaunošanās un reģenerācijas pētījumiem",
		},
		mk: { label: "Опоравок и регенерација", heading: "Пептиди за опоравок и регенерација" },
		nb: { label: "Restitusjon", heading: "Peptider for restitusjonsforskning" },
		nl: { label: "Herstel", heading: "Peptiden voor herstelonderzoek" },
		pl: { label: "Regeneracja", heading: "Peptydy do badań nad regeneracją" },
		pt: { label: "Recuperação", heading: "Péptidos para recuperação" },
		"pt-br": { label: "Recuperação", heading: "Peptídeos para pesquisa em recuperação" },
		ro: { label: "Recuperare", heading: "Peptide pentru cercetarea recuperării tisulare" },
		sk: { label: "Regenerácia", heading: "Regenerácia" },
		sl: { label: "Obnova in regeneracija", heading: "Peptidi za obnovo in regeneracijo" },
		sr: { label: "Oporavak i regeneracija", heading: "Peptidi za oporavak i regeneraciju" },
		sv: { label: "Återhämtning", heading: "Återhämtningspeptider" },
		tr: { label: "Toparlanma", heading: "Toparlanma araştırma peptitleri" },
	},
	cognition: {
		de: { label: "Kognition", heading: "Nootrope Peptide für die Forschung" },
		en: { label: "Cognition", heading: "Cognitive Research Peptides" },
		bg: { label: "Когнитивни функции", heading: "Ноотропни пептиди за изследвания" },
		bs: { label: "Kognicija", heading: "Peptidi za istraživanje kognicije" },
		cs: { label: "Kognice", heading: "Nootropní peptidy pro výzkum kognice" },
		da: { label: "Kognition", heading: "Neurobiologiske forskningspeptider" },
		el: { label: "Γνωστική λειτουργία", heading: "Πεπτίδια για έρευνα γνωστικής λειτουργίας" },
		es: { label: "Cognición", heading: "Péptidos para investigación de cognición" },
		et: { label: "Kognitiivsed funktsioonid", heading: "Kognitiivsete funktsioonide uurimispeptiidid" },
		fi: { label: "Kognitio", heading: "Kognitiotutkimuksen materiaalit" },
		fr: { label: "Cognition", heading: "Peptides pour la recherche sur la cognition" },
		hr: { label: "Kognicija", heading: "Peptidi za istraživanje kognicije" },
		hu: { label: "Kogníció", heading: "Peptidek kogníciókutatáshoz" },
		it: { label: "Funzioni cognitive", heading: "Peptidi per la ricerca sulle funzioni cognitive" },
		lt: { label: "Pažinimo funkcijos", heading: "Peptidai pažinimo funkcijų tyrimams" },
		lv: { label: "Kognitīvās funkcijas", heading: "Peptīdi kognitīvo funkciju pētījumiem" },
		mk: { label: "Когниција", heading: "Пептиди за истражување на когницијата" },
		nb: { label: "Kognisjon", heading: "Peptider for kognisjonsforskning" },
		nl: { label: "Cognitie", heading: "Peptiden voor cognitieonderzoek" },
		pl: { label: "Funkcje poznawcze", heading: "Peptydy do badań nad funkcjami poznawczymi" },
		pt: { label: "Cognição", heading: "Péptidos nootrópicos para investigação" },
		"pt-br": { label: "Cognição", heading: "Peptídeos para pesquisa em cognição" },
		ro: { label: "Cogniție", heading: "Peptide pentru cercetarea cogniției" },
		sk: { label: "Kognitívne funkcie", heading: "Kognitívne funkcie" },
		sl: { label: "Kognicija", heading: "Peptidi za raziskovanje kognicije" },
		sr: { label: "Kognicija", heading: "Peptidi za istraživanje kognicije" },
		sv: { label: "Kognition", heading: "Peptider för kognitionsforskning" },
		tr: { label: "Biliş", heading: "Biliş araştırma peptitleri" },
	},
	skin: {
		de: { label: "Haut und Haar", heading: "Haut- und Haarpeptide sowie Melanocortin-Analoga" },
		en: { label: "Skin", heading: "Skin and Hair Research Materials" },
		bg: { label: "Кожа и коса", heading: "Медни пептиди и изследвания на кожата" },
		bs: { label: "Koža i kosa", heading: "Peptidi za kožu i istraživanje pigmentacije" },
		cs: { label: "Kůže a vlasy", heading: "Měděné peptidy a výzkum kůže" },
		da: { label: "Hud og hår", heading: "Hud- og hårrelaterede forskningsmaterialer" },
		el: { label: "Δέρμα", heading: "Πεπτίδια χαλκού και δερματική έρευνα" },
		es: {
			label: "Piel, cabello y pigmentación",
			heading: "Péptidos para investigación de piel, cabello y pigmentación",
		},
		et: { label: "Nahk ja juuksed", heading: "Naha ja juuste uurimispeptiidid" },
		fi: { label: "Iho", heading: "Ihon ja hiusten tutkimusmateriaalit" },
		fr: { label: "Peau", heading: "Peptides pour la recherche sur la peau et les cheveux" },
		hr: { label: "Koža i kosa", heading: "Peptidi za kožu i istraživanje pigmentacije" },
		hu: { label: "Bőr és haj", heading: "Peptidek bőr- és pigmentációkutatáshoz" },
		it: { label: "Pelle", heading: "Peptidi per la ricerca cutanea" },
		lt: { label: "Oda ir plaukai", heading: "Peptidai odos ir plaukų tyrimams" },
		lv: { label: "Āda un mati", heading: "Peptīdi ādas un matu pētījumiem" },
		mk: { label: "Кожа и коса", heading: "Пептиди за кожа и истражување на пигментацијата" },
		nb: { label: "Hud", heading: "Kobberpeptider og melanokortinanaloger for hudforskning" },
		nl: { label: "Huid", heading: "Peptiden voor huidonderzoek" },
		pl: { label: "Skóra i włosy", heading: "Peptydy miedziowe i melanokortynowe" },
		pt: { label: "Pele e cabelo", heading: "Péptidos para pele e cabelo" },
		"pt-br": { label: "Pele e cabelo", heading: "Peptídeos para pesquisa sobre pele e cabelo" },
		ro: { label: "Piele", heading: "Peptide pentru cercetarea pielii și pigmentării" },
		sk: { label: "Pokožka a vlasy", heading: "Pokožka a vlasy" },
		sl: { label: "Koža in lasje", heading: "Peptidi za kožo in raziskovanje pigmentacije" },
		sr: { label: "Koža i kosa", heading: "Peptidi za kožu i istraživanje pigmentacije" },
		sv: { label: "Hud och hår", heading: "Kopparpeptider och melanokortinanaloger" },
		tr: { label: "Cilt ve saç", heading: "Cilt ve saç araştırma peptitleri" },
	},
	"anti-aging": {
		de: { label: "Anti-Aging", heading: "Anti-Aging- und Longevity-Forschung" },
		en: { label: "Anti-Aging", heading: "Anti-Aging Research Materials" },
		bg: { label: "Изследвания на стареенето", heading: "Материали за изследвания на стареенето" },
		bs: { label: "Dugovječnost i starenje", heading: "Peptidi dugovječnosti i istraživanje starenja" },
		cs: { label: "Výzkum stárnutí", heading: "Anti-aging peptidy a výzkum dlouhověkosti" },
		da: { label: "Anti-aging", heading: "Materialer til aldringsforskning" },
		el: { label: "Γήρανση και μακροζωία", heading: "Πεπτίδια και υλικά έρευνας για τη γήρανση" },
		es: { label: "Antienvejecimiento", heading: "Péptidos para investigación de longevidad" },
		et: { label: "Pikaajalisus ja vananemine", heading: "Pikaajalisuse ja vananemise uurimispeptiidid" },
		fi: { label: "Anti-aging", heading: "Anti-aging- ja pitkäikäisyystutkimus" },
		fr: { label: "Anti-âge", heading: "Matériaux de recherche anti-âge" },
		hr: { label: "Dugovječnost i starenje", heading: "Peptidi dugovječnosti i istraživanje starenja" },
		hu: { label: "Anti-aging", heading: "Peptidek és kofaktorok öregedéskutatáshoz" },
		it: {
			label: "Longevità e invecchiamento",
			heading: "Peptidi per la ricerca su longevità e invecchiamento",
		},
		lt: { label: "Ilgaamžiškumas ir senėjimas", heading: "Peptidai ilgaamžiškumo ir senėjimo tyrimams" },
		lv: { label: "Ilgmūžība un novecošana", heading: "Peptīdi ilgmūžības un novecošanas pētījumiem" },
		mk: { label: "Долговечност и стареење", heading: "Пептиди за долговечност и истражување на стареењето" },
		nb: { label: "Aldringsforskning", heading: "Peptider og materialer for aldringsforskning" },
		nl: { label: "Anti-aging", heading: "Materialen voor anti-agingonderzoek" },
		pl: { label: "Anti-aging", heading: "Materiały do badań nad starzeniem" },
		pt: { label: "Longevidade", heading: "Péptidos de longevidade" },
		"pt-br": { label: "Longevidade", heading: "Peptídeos para pesquisa em longevidade" },
		ro: { label: "Longevitate", heading: "Peptide și molecule pentru cercetarea longevității" },
		sk: { label: "Dlhovekosť a starnutie", heading: "Dlhovekosť a starnutie" },
		sl: { label: "Dolgoživost in staranje", heading: "Peptidi dolgoživosti in raziskovanje staranja" },
		sr: { label: "Dugovečnost i starenje", heading: "Peptidi dugovečnosti i istraživanje starenja" },
		sv: { label: "Anti-aging", heading: "Anti-aging och åldrandeforskning" },
		tr: { label: "Yaşlanma araştırmaları", heading: "Yaşlanma araştırmaları için peptitler" },
	},
	hormonal: {
		de: { label: "Hormonelle Signalwege", heading: "Peptide für hormonelle und neuropeptiderge Forschung" },
		en: { label: "Hormonal", heading: "Hormonal Research Peptides" },
		bg: { label: "Хормонална сигнализация", heading: "Материали за хормонална сигнализация" },
		bs: { label: "Hormonska signalizacija", heading: "Peptidi za hormonsku signalizaciju" },
		cs: { label: "Hormonální signalizace", heading: "Peptidy hormonální a neuropeptidergní signalizace" },
		da: { label: "Hormonel signalering", heading: "Peptider til hormonel signalering" },
		el: { label: "Ορμονική σηματοδότηση", heading: "Πεπτίδια ορμονικής σηματοδότησης" },
		es: { label: "Señalización hormonal", heading: "Péptidos de señalización hormonal" },
		et: { label: "Hormonaalne signalisatsioon", heading: "Hormonaalse signalisatsiooni uurimispeptiidid" },
		fi: { label: "Hormonisignalointi", heading: "Hormonisignaloinnin tutkimusmateriaalit" },
		fr: { label: "Signalisation hormonale", heading: "Peptides et signalisation hormonale" },
		hr: { label: "Hormonska signalizacija", heading: "Peptidi za hormonsku signalizaciju" },
		hu: { label: "Jelátvitel", heading: "Peptidek hormonális jelátvitel kutatásához" },
		it: { label: "Segnalazione ormonale", heading: "Peptidi per la ricerca sulla segnalazione ormonale" },
		lt: { label: "Hormonų signalizacija", heading: "Peptidai hormonų signalizacijos tyrimams" },
		lv: { label: "Hormonālā signalizācija", heading: "Peptīdi hormonālās signalizācijas pētījumiem" },
		mk: { label: "Хормонална сигнализација", heading: "Пептиди за хормонална сигнализација" },
		nb: { label: "Hormonell signalering", heading: "Peptider for forskning på hormonell signalering" },
		nl: { label: "Hormonale signalering", heading: "Peptiden voor hormonaal signaalonderzoek" },
		pl: { label: "Sygnalizacja hormonalna", heading: "Materiały do badań nad sygnalizacją hormonalną" },
		pt: { label: "Sinalização hormonal", heading: "Péptidos de sinalização hormonal" },
		"pt-br": { label: "Sinalização hormonal", heading: "Peptídeos para pesquisa em sinalização hormonal" },
		ro: { label: "Hormonal", heading: "Peptide pentru semnalizarea hormonală" },
		sk: { label: "Hormonálna signalizácia", heading: "Hormonálna signalizácia" },
		sl: { label: "Hormonska signalizacija", heading: "Peptidi za hormonsko signalizacijo" },
		sr: { label: "Hormonska signalizacija", heading: "Peptidi za hormonsku signalizaciju" },
		sv: { label: "Hormonell signalering", heading: "Peptider för hormonell signalering" },
		tr: { label: "Hormonal sinyalleşme", heading: "Hormonal sinyalleşme araştırma peptitleri" },
	},
	immune: {
		de: "Immunsystem",
		en: "Immune System",
		bg: "Имунна система",
		bs: "Imunski sistem",
		cs: "Imunitní systém",
		da: "Immunforsvar",
		el: "Ανοσοποιητικό σύστημα",
		es: "Sistema inmunitario",
		et: "Immuunsüsteem",
		fi: "Immuunijärjestelmä",
		fr: "Système immunitaire",
		hr: "Imunosni sustav",
		hu: "Immunrendszer",
		it: "Sistema immunitario",
		lt: "Imuninė sistema",
		lv: "Imūnā sistēma",
		mk: "Имун систем",
		nb: "Immunforsvar",
		nl: "Immuunsysteem",
		pl: "Układ odpornościowy",
		pt: "Sistema imunitário",
		"pt-br": "Sistema imunológico",
		ro: "Sistem imunitar",
		sk: "Imunitný systém",
		sl: "Imunski sistem",
		sr: "Imunski sistem",
		sv: "Immunförsvar",
		tr: "Bağışıklık Sistemi",
	},
};

export function isResearchAreaSlug(value: string): value is ResearchAreaSlug {
	return (RESEARCH_AREA_SLUGS as readonly string[]).includes(value);
}

/** Localized research-area label; walks the content chain (es-mx → es), then English, then the slug. */
export function getResearchAreaLabel(slug: string, locale: string): string {
	if (!isResearchAreaSlug(slug)) return slug;
	return resolveResearchAreaText(pickResearchAreaText(slug, locale), "label");
}

/** A specific page heading can differ from the concise navigation label. */
export function getResearchAreaHeading(slug: string, locale: string): string {
	if (!isResearchAreaSlug(slug)) return slug;
	return resolveResearchAreaText(pickResearchAreaText(slug, locale), "heading");
}

/** The entry of the locale's content chain; a regional locale (es-mx) reads its base (es). */
function pickResearchAreaText(slug: ResearchAreaSlug, locale: string): ResearchAreaText {
	const labels = RESEARCH_AREA_LABELS[slug];
	for (const candidate of getContentFallbackChainOf(locale)) {
		const text = labels[candidate];
		if (text != null) return text;
	}
	return labels.en ?? slug;
}

/** Existing string entries retain identical navigation and heading text. */
function resolveResearchAreaText(value: ResearchAreaText, field: "label" | "heading"): string {
	return typeof value === "string" ? value : field === "heading" ? value.heading || value.label : value.label;
}
